import { useCallback, useEffect, useRef } from "react";
import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { useIsStandalonePage } from "@/hooks/ui/useIsStandalonePage";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useCreateAccountStore } from "@/store/accountStore";
import { useConnectStore, FLOW_TYPES, STEPS } from "@/store/connectStore";
import { useOnboardingStore, ONBOARDING_STEPS, OnboardingStepType } from "@/store/onboardingStore";
import { hasExchangeAccounts, hasSocialAccounts } from "@/utils/onboarding";
import { useSnackbar } from "notistack";

// Global completion tracking to prevent multiple instances from firing
let globalCompletionTracked = false;

export const useOnboarding = () => {
  const { isTMA } = useTelegramContext();
  const isStandalonePage = useIsStandalonePage();
  const { enqueueSnackbar } = useSnackbar();
  const { login, hubUser, authenticated } = useHubUser();
  const isOnboardingEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.IMPROVE_UX_HUB_NEW_ONBOARDING);
  const { setOpenConnectModal, setActiveStep, setExchangeIds, setFlowType } = useConnectStore();
  const { socialAccounts } = useSocialAccounts();
  const { updateFormData } = useCreateAccountStore();
  const oAuthSupportMap = useOAuthSupportMap();
  const { trackEvent } = useMetricsEvents();

  // Track if navigation was manual (to prevent smart progression interference)
  const isManualNavigation = useRef(false);

  // Track previous isOpen state to detect when modal closes
  const prevIsOpenRef = useRef(false);

  // Use refs for immediate protection against multiple calls
  const hasAutoProgressedAfterWalletRef = useRef(false);

  const {
    isOpen,
    setIsOpen,
    currentStep,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    canGoNext,
    canGoPrevious,
    reset,
    isOnboardingCompleted,
    setOnboardingCompleted,
  } = useOnboardingStore();

  const openOnboarding = useCallback(() => {
    // Track when user starts the onboarding flow
    trackEvent(METRICS_EVENTS.ONBOARDING_STARTED, {});
    // Always start from the first step when opening onboarding
    setCurrentStep(ONBOARDING_STEPS.CREATE_ACCOUNT);
    // Reset flags when opening onboarding
    globalCompletionTracked = false;
    hasAutoProgressedAfterWalletRef.current = false;
    prevIsOpenRef.current = false; // Reset previous state
    setIsOpen(true);
  }, [setIsOpen, setCurrentStep, trackEvent]);

  const closeOnboarding = useCallback(() => {
    // Track when user closes the onboarding modal without completing it
    if (!isOnboardingCompleted) {
      trackEvent(METRICS_EVENTS.ONBOARDING_CLOSED, {});
    }
    setIsOpen(false);
  }, [setIsOpen, trackEvent, isOnboardingCompleted]);

  const handleWalletConnect = useCallback(
    async (walletType: "evm" | "ton") => {
      if (walletType === "ton" && !isTMA) {
        enqueueSnackbar("TON wallet connection will be available soon", { variant: "info" });
        return false;
      }

      try {
        login();
        // Smart progression will happen via useEffect when hubUser updates
        return true;
      } catch {
        enqueueSnackbar("Failed to connect wallet", { variant: "error" });
        return false;
      }
    },
    [isTMA, enqueueSnackbar, login],
  );

  const handleCEXSelection = useCallback(
    (exchangeId: string) => {
      // Track when user selects a centralized exchange during onboarding
      trackEvent(METRICS_EVENTS.ONBOARDING_CEX_SELECTED, {
        exchange_id: exchangeId,
        connection_type: oAuthSupportMap[exchangeId] ? "oauth" : "api_keys",
      });

      // Pre-select the exchange in the form data
      updateFormData({ selectedCex: exchangeId });

      // Set up the connect modal for the selected exchange
      setFlowType(FLOW_TYPES.ONBOARDING);
      setExchangeIds([exchangeId]);

      // Determine the correct step based on OAuth support
      const step = oAuthSupportMap[exchangeId] ? STEPS.LinkAccount : STEPS.ApiKeys;
      setActiveStep(step);
      setOpenConnectModal(true);

      // Smart progression will happen via useEffect when exchangeAccounts updates
    },
    [updateFormData, setFlowType, setExchangeIds, setActiveStep, setOpenConnectModal, oAuthSupportMap, trackEvent],
  );

  const completeOnboarding = useCallback(() => {
    // Just close the modal and mark as completed - tracking happens in useEffect
    setOnboardingCompleted(true);
    setIsOpen(false);
  }, [setOnboardingCompleted, setIsOpen]);

  const shouldShowOnboarding = useCallback(() => {
    // Feature flag check - if onboarding is disabled, don't show it
    if (!isOnboardingEnabled) return false;

    // Standalone pages don't render the onboarding modal
    if (isStandalonePage) return false;

    // For TON on TMA, don't show onboarding
    if (isTMA) return false;

    // For Privy (non-TMA), show onboarding always
    return true;
  }, [isOnboardingEnabled, isStandalonePage, isTMA]);

  const resetOnboardingState = useCallback(() => {
    reset();
    setOnboardingCompleted(false);
  }, [reset, setOnboardingCompleted]);

  // Add skip step functionality
  const handleSkipStep = useCallback(
    (step: OnboardingStepType) => {
      // Track when user skips an onboarding step
      trackEvent(METRICS_EVENTS.ONBOARDING_SKIP_CLICKED, {
        step: step,
      });

      // If this is the last step, complete onboarding instead of going to next step
      if (canGoNext()) {
        goToNextStep();
      } else {
        completeOnboarding();
      }
    },
    [canGoNext, goToNextStep, completeOnboarding, trackEvent],
  );

  // Track completion when modal closes (from open to closed) - only once
  useEffect(() => {
    // Detect when modal closes (was open, now closed) and completion hasn't been tracked yet
    if (prevIsOpenRef.current && !isOpen && !globalCompletionTracked && isOnboardingCompleted) {
      globalCompletionTracked = true;
      // Track when user successfully completes the entire onboarding flow
      trackEvent(METRICS_EVENTS.ONBOARDING_COMPLETED, {});
    }

    // Update previous state AFTER checking
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isOnboardingCompleted, trackEvent]);

  // Smart progression logic - only moves forward once when wallet gets connected
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Don't interfere with manual navigation
    if (isManualNavigation.current) {
      isManualNavigation.current = false;
      return;
    }

    // Only auto-progress once after wallet connection
    if (
      authenticated &&
      hubUser &&
      currentStep === ONBOARDING_STEPS.CREATE_ACCOUNT &&
      !hasAutoProgressedAfterWalletRef.current
    ) {
      hasAutoProgressedAfterWalletRef.current = true;

      // Check what the user needs next
      const userHasExchangeAccounts = hasExchangeAccounts(hubUser);
      const userHasSocialAccounts = hasSocialAccounts(socialAccounts);

      if (!userHasExchangeAccounts) {
        setCurrentStep(ONBOARDING_STEPS.LINK_CEX);
      } else if (!userHasSocialAccounts) {
        setCurrentStep(ONBOARDING_STEPS.CONNECT_SOCIAL);
      } else {
        // User has completed everything - close onboarding
        completeOnboarding();
      }
    }
  }, [isOpen, authenticated, hubUser, socialAccounts.length, currentStep, setCurrentStep, completeOnboarding]);

  // Manual navigation functions - these set a flag to prevent smart progression interference
  const manualGoToNextStep = useCallback(() => {
    // Track when user manually clicks continue/next button in onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_CONTINUE_CLICKED, {
      from_step: currentStep,
    });
    isManualNavigation.current = true;
    goToNextStep();
  }, [goToNextStep, currentStep, trackEvent]);

  const manualGoToPreviousStep = useCallback(() => {
    // Track when user manually clicks back button in onboarding
    trackEvent(METRICS_EVENTS.ONBOARDING_BACK_CLICKED, {
      from_step: currentStep,
    });
    isManualNavigation.current = true;
    goToPreviousStep();
  }, [goToPreviousStep, currentStep, trackEvent]);

  const manualSetCurrentStep = useCallback(
    (step: OnboardingStepType) => {
      isManualNavigation.current = true;
      setCurrentStep(step);
    },
    [setCurrentStep],
  );

  return {
    // State
    isOpen,
    currentStep,
    isOnboardingCompleted,

    // Computed state
    canGoNext,
    canGoPrevious,
    shouldShowOnboarding: shouldShowOnboarding(),

    // Actions
    openOnboarding,
    closeOnboarding,
    setCurrentStep: manualSetCurrentStep,
    goToNextStep: manualGoToNextStep,
    goToPreviousStep: manualGoToPreviousStep,
    reset,
    resetOnboardingState,
    handleSkipStep,

    // Step handlers
    handleWalletConnect,
    handleCEXSelection,
    completeOnboarding,
  };
};

export type UseOnboardingReturn = ReturnType<typeof useOnboarding>;
